import apiClient from './apiClient';
import type { ApiResponse } from '../types/api';

/**
 * A doctor the authenticated "Asistente Médico" is actively assigned to
 * (clinical-delegation R2, R3). Empty for any user who is not a consented
 * assistant — the frontend must stay inert in that case, not show errors.
 */
export interface AssignedDoctor {
  id: number;
  full_name: string;
  specialty: string | null;
  can_sign: boolean;
  /**
   * Whether the assistant may write for this doctor right now: the backend's
   * canActAsDoctor (consultations delegation domain enabled, assistant
   * profile, active assignment). An assignment alone is not enough.
   */
  can_act: boolean;
  /**
   * Whether the assistant may sign for this doctor right now: consent AND
   * the delegated-signing legal flag (off by default) AND can_act.
   */
  can_sign_delegated: boolean;
}

/**
 * A draft consultation pending the doctor's signature (clinical-record-signing
 * R7): the doctor's own open drafts and drafts recorded by assistants share
 * one queue, oldest first.
 */
export interface PendingSignature {
  id: number;
  consultation_number: string | null;
  patient_id: number;
  recorded_by: number | null;
  recorded_by_name: string | null;
  is_own: boolean;
  created_at: string;
  age_hours: number;
}

/** One of the doctor's active assistants, with its delegated-signing consent (R7). */
export interface MyAssistant {
  id: number;
  assistant_user_id: number;
  assistant_name: string | null;
  can_sign: boolean;
  /** Whether the consent has any effect yet (legal flag, domain, profile). */
  can_sign_effective: boolean;
  granted_at: string;
}

/** A consultation of the doctor's signed by an assistant on their behalf (R13). */
export interface DelegatedSignature {
  id: number;
  consultation_number: string | null;
  status: 'signed' | 'amended';
  signed_at: string;
  signed_by_name: string | null;
}

export const clinicalDelegationService = {
  /**
   * Doctors assigned to the current assistant. Safe to call for any user —
   * returns an empty array (never throws) when the caller has no active
   * assignments, so callers do not need to know their own profile first.
   */
  async getMyDoctors(): Promise<AssignedDoctor[]> {
    try {
      const response = await apiClient.get<ApiResponse<AssignedDoctor[]>>('/clinical-delegation/my-doctors');
      return response.data.data || [];
    } catch {
      return [];
    }
  },

  /**
   * Pending-signature queue for the current doctor. 403s for non-doctors —
   * callers should only invoke this when `user.doctor` is set.
   */
  async getPendingSignatures(): Promise<PendingSignature[]> {
    const response = await apiClient.get<ApiResponse<PendingSignature[]>>('/clinical-delegation/pending-signatures');
    return response.data.data || [];
  },

  /** The current doctor's assistants (403 for non-doctors). */
  async getMyAssistants(): Promise<MyAssistant[]> {
    const response = await apiClient.get<ApiResponse<MyAssistant[]>>('/clinical-delegation/my-assistants');
    return response.data.data || [];
  },

  /** The doctor grants or withdraws one assistant's delegated signing (audited server-side, R8). */
  async setAssistantSigning(assignmentId: number, canSign: boolean): Promise<MyAssistant> {
    const response = await apiClient.put<ApiResponse<MyAssistant>>(`/clinical-delegation/my-assistants/${assignmentId}/signing`, { can_sign: canSign });
    return response.data.data;
  },

  /** The current doctor's consultations signed by delegation, newest first. */
  async getDelegatedSignatures(): Promise<DelegatedSignature[]> {
    const response = await apiClient.get<ApiResponse<DelegatedSignature[]>>('/clinical-delegation/delegated-signatures');
    return response.data.data || [];
  },
};

export default clinicalDelegationService;
